import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Login from './components/Login';
import RequestPage from './components/Automation/Application/RequestPage';
import EditApplicationPage from "./components/Automation/Application/EditApplicationPage";
import ApplicationList from './components/Automation/ApplicationList/ApplicationList';
import StudentList from './components/Automation/StudentList/StudentList';
import ApplicationMonitoringPage from './pages/monitoring';
import ApplicationDetailsPage from './pages/monitoring/ApplicationDetailsPage';
import SettingsPage from './pages/SettingsPage';
import DepartmentsPage from './pages/DepartmentsPage';
import ProgramsPage from './pages/ProgramsPage';
import DraftsListPage from './components/Automation/Application/DraftsListPage';
import EditDraftPage from './components/Automation/Application/EditDraftPage';
import TemplatesList from './pages/documents/TemplatesList';
import NewTemplate from './pages/documents/NewTemplate';
import TemplateMapping from './pages/documents/TemplateMapping';
import GenerateDocument from './pages/documents/GenerateDocument';
import GeneratedDocumentsList from './pages/documents/GeneratedDocumentsList';
import AccessManagement from './pages/access/AccessManagement';
import AccessSettingsPage from './pages/access/AccessSettingsPage';
import DocumentTypesList from './pages/documents/DocumentTypesList';
import DocumentTypeForm from './pages/documents/DocumentTypeForm';

// Импорт страницы Уведомлений
import CompletionNotificationsPage from './pages/CompletionNotificationsPage';

// Новые импорты Заказчиков и Исполнителей
import CustomersListPage from './pages/organizations/customer/CustomersListPage';
import CustomerFormPage from './pages/organizations/customer/CustomerFormPage';
import ExecutorsListPage from './pages/organizations/executor/ExecutorsListPage';

import { TitleProvider } from './contexts/TitleContext';
import { ToastProvider } from './contexts/ToastContext';
import MainLayout from './layouts/MainLayout';

interface UserData {
  email: string;
  full_name: string;
  id: number;
  is_active: boolean;
  role: {
    id: number;
    name: string;
    description: string;
  } | string;
}

const App: React.FC = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState<UserData | null>(null);

  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  const handleLogout = useCallback(() => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    setUser(null);
    setIsAuthenticated(false);
  }, []);

  useEffect(() => {
    const checkAuthStatus = async () => {
      const token = localStorage.getItem("token");
      if (token) {
        try {
          const response = await fetch(`${API_BASE_URL}/api/v1/auth/me`, {
            headers: { Authorization: token },
          });

          if (response.ok) {
            const userData = await response.json();
            localStorage.setItem('user', JSON.stringify(userData));
            setUser(userData);
            setIsAuthenticated(true);
          } else {
            handleLogout();
          }
        } catch (error) {
          console.error("Ошибка проверки авторизации:", error);
          handleLogout();
        }
      }
      setIsLoading(false);
    };

    checkAuthStatus();
  }, [API_BASE_URL, handleLogout]);

  const handleLogin = () => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
        setUser(JSON.parse(storedUser));
    }
    setIsAuthenticated(true);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Загрузка...</p>
        </div>
      </div>
    );
  }

  const username = user?.full_name || user?.email || '—';

  let roleName = 'user';
  if (user?.role) {
    if (typeof user.role === 'string') {
        roleName = user.role;
    } else if (typeof user.role === 'object' && user.role.name) {
        roleName = user.role.name;
    }
  }

  const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
    if (!isAuthenticated) return <Navigate to="/login" replace />;

    return (
      <MainLayout
        role={roleName}
        username={username}
        onLogout={handleLogout}
      >
        {children}
      </MainLayout>
    );
  };

  return (
    <TitleProvider defaultTitle="УПРАВЛЕНИЕ-СДО.РФ">
      <ToastProvider>
          <Router>
            <Routes>
              {/* Публичный роут: Логин */}
              <Route
                path="/login"
                element={!isAuthenticated ? <Login onLogin={handleLogin} /> : <Navigate to="/applications" replace />}
              />

              {/* Индексный роут */}
              <Route
                path="/"
                element={<Navigate to={isAuthenticated ? "/applications" : "/login"} replace />}
              />

              {/* Защищенные роуты внутри MainLayout */}
              <Route
                path="/application/new"
                element={<ProtectedRoute><RequestPage onLogout={handleLogout} /></ProtectedRoute>}
              />
              <Route
                path="/application/edit/:applicationId"
                element={<ProtectedRoute><EditApplicationPage onLogout={handleLogout} /></ProtectedRoute>}
              />
              <Route
                path="/drafts"
                element={<ProtectedRoute><DraftsListPage onLogout={handleLogout} /></ProtectedRoute>}
              />
              <Route
                path="/drafts/:draftId"
                element={<ProtectedRoute><EditDraftPage onLogout={handleLogout} /></ProtectedRoute>}
              />
              <Route
                path="/monitoring"
                element={
                  <ProtectedRoute>
                    <ApplicationMonitoringPage
                      onLogout={handleLogout}
                      username={username}
                      role={roleName}
                    />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/monitoring/:applicationId"
                element={
                  <ProtectedRoute>
                    <ApplicationDetailsPage onLogout={handleLogout} />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/applications"
                element={<ProtectedRoute><ApplicationList onLogout={handleLogout} /></ProtectedRoute>}
              />

              {/* Маршрут логов уведомлений конкретной заявки */}
              <Route
                path="/applications/:applicationId/notifications"
                element={<ProtectedRoute><CompletionNotificationsPage onLogout={handleLogout} /></ProtectedRoute>}
              />

              <Route
                path="/students"
                element={<ProtectedRoute><StudentList /></ProtectedRoute>}
              />
              <Route
                path="/departments"
                element={<ProtectedRoute><DepartmentsPage onLogout={handleLogout} username={username} role={roleName} /></ProtectedRoute>}
              />

              {/* Маршруты для Организаций (Заказчики и Исполнители) */}
              <Route
                path="/organizations"
                element={<Navigate to="/organizations/customers" replace />}
              />
              <Route
                path="/organizations/customers"
                element={<ProtectedRoute><CustomersListPage /></ProtectedRoute>}
              />
              <Route
                path="/organizations/customers/:id"
                element={<ProtectedRoute><CustomerFormPage /></ProtectedRoute>}
              />
              <Route
                path="/organizations/executors"
                element={<ProtectedRoute><ExecutorsListPage /></ProtectedRoute>}
              />

              <Route
                path="/settings"
                element={<ProtectedRoute><SettingsPage onLogout={handleLogout} username={username} role={roleName} /></ProtectedRoute>}
              />
              <Route
                path="/programs"
                element={<ProtectedRoute><ProgramsPage /></ProtectedRoute>}
              />

              <Route
                path="/documents/types"
                element={<ProtectedRoute><DocumentTypesList /></ProtectedRoute>}
              />

              <Route
                path="/documents/types/new"
                element={<ProtectedRoute><DocumentTypeForm /></ProtectedRoute>}
              />
              <Route
                path="/documents/types/edit/:id"
                element={<ProtectedRoute><DocumentTypeForm /></ProtectedRoute>}
              />

              <Route
                path="/documents/templates"
                element={<ProtectedRoute><TemplatesList /></ProtectedRoute>}
              />
              <Route
                path="/documents/templates/new"
                element={<ProtectedRoute><NewTemplate /></ProtectedRoute>}
              />

              <Route
                path="/documents/history"
                element={<ProtectedRoute><GeneratedDocumentsList /></ProtectedRoute>}
              />

              <Route
                path="/documents/templates/:id/mapping"
                element={<ProtectedRoute><TemplateMapping /></ProtectedRoute>}
              />

              <Route
                path="/applications/:applicationId/documents/generate"
                element={<ProtectedRoute><GenerateDocument /></ProtectedRoute>}
              />

              <Route
                path="/access"
                element={
                  <ProtectedRoute>
                    {roleName === 'admin' ? (
                      <AccessManagement />
                    ) : (
                      <Navigate to="/applications" replace />
                    )}
                  </ProtectedRoute>
                }
              />

              <Route
                path="/access/:id"
                element={
                  <ProtectedRoute>
                    {roleName === 'admin' ? (
                      <AccessSettingsPage />
                    ) : (
                      <Navigate to="/applications" replace />
                    )}
                  </ProtectedRoute>
                }
              />

              {/* Редирект для всех остальных путей */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Router>
      </ToastProvider>
    </TitleProvider>
  );
};

export default App;